import { achievementBadges } from '@/fixtures/badges';
import { normalizePhone } from '@/util/phone';
import type { AchievementBadge } from '@/types/domain';
import type { LocationSelection } from '@/components/locationCascade/locationCascade';
import { clone, fakeDelay } from './serviceUtil';
import { api, ApiError } from './apiClient';

export interface CurrentUser {
  id: number;
  name: string;
  /** Null for an account registered with a phone number and no email. */
  email: string | null;
  phone: string;
  city: string | null;
  district: string | null;
  countyCode: number | null;
  townId: number | null;
  neighbourhoodId: number | null;
  neighbourhood: string | null;
  role: 'user' | 'admin';
  avatarUrl: string | null;
  isNamePrivate: boolean;
  isPhonePrivate: boolean;
  emailVerified: boolean;
  /** Null for every account created before phone verification shipped. */
  phoneVerified: boolean;
  /** false while the user has taken their own account passive. */
  isActive: boolean;
  /** Google-created accounts have none; deletion asks for Google instead. */
  hasPassword: boolean;
}

/** Wire shape of the user object in /auth/login and /auth/me responses. */
interface ApiUser {
  id: number;
  name: string;
  email: string | null;
  phone: string;
  city: string | null;
  district: string | null;
  county_code: number | null;
  town_id: number | null;
  neighbourhood_id: number | null;
  neighbourhood: string | null;
  role: 'user' | 'admin';
  avatar_url: string | null;
  is_name_private: boolean;
  is_phone_private: boolean;
  email_verified: boolean;
  phone_verified?: boolean;
  is_active: boolean;
  has_password: boolean;
}

const toCurrentUser = (u: ApiUser): CurrentUser => ({
  id: u.id,
  name: u.name,
  email: u.email,
  phone: u.phone,
  city: u.city,
  district: u.district,
  countyCode: u.county_code,
  townId: u.town_id,
  neighbourhoodId: u.neighbourhood_id,
  neighbourhood: u.neighbourhood,
  role: u.role,
  avatarUrl: u.avatar_url,
  isNamePrivate: u.is_name_private,
  isPhonePrivate: u.is_phone_private,
  emailVerified: u.email_verified,
  phoneVerified: Boolean(u.phone_verified),
  isActive: u.is_active !== false,
  hasPassword: Boolean(u.has_password),
});

/**
 * Session probe — null means guest (401), never throws for that case.
 *
 * `announceExpiry` decides whether an expired session is allowed to say so.
 * At boot nobody is known to be signed in, so "no session" is an ordinary
 * answer and announcing it would greet every returning visitor with a notice.
 * Re-checking a user the app currently believes is signed in is the opposite
 * case: they are about to be moved to the login screen, and being moved there
 * with no explanation is the confusion this whole change exists to remove.
 */
export async function getCurrentUser(
  { announceExpiry = false }: { announceExpiry?: boolean } = {}
): Promise<CurrentUser | null> {
  try {
    const { user } = await api<{ user: ApiUser }>('/auth/me', {
      silentRevocation: !announceExpiry,
    });
    return toCurrentUser(user);
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

/** `identifier` is an email or a Turkish mobile in any shape; the API resolves which. */
export async function login(identifier: string, password: string): Promise<CurrentUser> {
  const { user } = await api<{ user: ApiUser }>('/auth/login', {
    method: 'POST',
    body: { identifier, password },
  });
  return toCurrentUser(user);
}

export interface RegisterInput {
  fullName: string;
  /** Optional — a phone number alone registers an account. */
  email?: string;
  phone: string;
  password: string;
}

/** Mirrors the backend password zod schema (8+, upper, lower, digit, special). */
export const isValidPassword = (password: string): boolean =>
  password.length >= 8 &&
  /[A-Z]/.test(password) &&
  /[a-z]/.test(password) &&
  /[0-9]/.test(password) &&
  /[^A-Za-z0-9]/.test(password);

/**
 * A registration in flight. No account exists yet — the backend is holding the
 * details until the SMS code comes back.
 *
 * The two timestamps come from the server rather than as durations: the code
 * outlives the countdown (delivery can take up to three minutes), and a client
 * clock that is minutes off would otherwise show a timer the server disagrees
 * with.
 */
export interface PendingRegistration {
  pendingId: string;
  phoneMasked: string;
  codeExpiresAt: string;
  resendAvailableAt: string;
  sendsRemaining: number;
}

export async function register(input: RegisterInput): Promise<PendingRegistration> {
  return api<PendingRegistration>('/auth/register', {
    method: 'POST',
    body: {
      name: input.fullName,
      // Omitted entirely when blank rather than sent as "": the field is
      // optional, and an empty string is not an address the API should judge.
      ...(input.email?.trim() ? { email: input.email.trim() } : {}),
      password: input.password,
      phone: normalizePhone(input.phone) ?? input.phone,
      kvkk_accepted: true, // checkbox is required in the form
    },
  });
}

/** Confirms the code and creates the account; the session cookie arrives here. */
export async function verifyRegistration(pendingId: string, code: string): Promise<CurrentUser> {
  const { user } = await api<{ user: ApiUser }>('/auth/register/verify', {
    method: 'POST',
    body: { pendingId, code },
  });
  return toCurrentUser(user);
}

export async function resendRegistrationCode(pendingId: string): Promise<PendingRegistration> {
  return api<PendingRegistration>('/auth/register/resend', {
    method: 'POST',
    body: { pendingId },
  });
}

/**
 * Where the Gmail button sends the browser. A full-page navigation, not a
 * fetch: Google's consent screen refuses to render inside an XHR, and the whole
 * flow ends with the backend redirecting us back with a cookie already set.
 *
 * Relative on purpose — in dev Vite proxies /rest to the backend, in production
 * they share an origin, so the same path works for both.
 */
export const GOOGLE_SIGNIN_URL = '/rest/v1/auth/google';

/**
 * Re-auth for deletion: only the signed-in user's own linked Google account earns a short-lived
 * `reauth` token on /settings; no session is issued.
 */
export const GOOGLE_REAUTH_URL = '/rest/v1/auth/google?purpose=delete';

/**
 * Finish a Google sign-up with the fields Google cannot supply. The account is
 * NOT created here any more — this sends the SMS code and returns the same
 * pending handle the password path uses, so both end on the same screen.
 */
export async function completeGoogleSignup(
  token: string,
  phone: string
): Promise<PendingRegistration> {
  return api<PendingRegistration>('/auth/google/complete', {
    method: 'POST',
    body: {
      token,
      phone: normalizePhone(phone) ?? phone,
      kvkk_accepted: true, // checkbox is required in the form
    },
  });
}

export async function resendVerificationEmail(email: string): Promise<void> {
  await api('/auth/resend-verification', { method: 'POST', body: { email } });
}

export async function forgotPassword(email: string): Promise<void> {
  await api('/auth/forget-password', { method: 'POST', body: { email } });
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await api('/auth/reset-password', { method: 'POST', body: { token, newPassword } });
}

/**
 * Şifremi Unuttum by SMS, for accounts with no email address.
 *
 * Unlike the email branch this one reports an unregistered number (404) instead
 * of answering the same way regardless, so a mistyped number is visible rather
 * than a code that never arrives. Calling it again resends.
 */
export interface PhoneResetHandle {
  phoneMasked: string;
  codeExpiresAt: string;
  resendAvailableAt: string;
  sendsRemaining: number;
}

export async function forgotPasswordByPhone(phone: string): Promise<PhoneResetHandle> {
  return api<PhoneResetHandle>('/auth/forget-password/phone', {
    method: 'POST',
    body: { phone: normalizePhone(phone) ?? phone },
  });
}

/** Trades the SMS code for the token the reset screen already understands. */
export async function verifyPhoneReset(phone: string, code: string): Promise<string> {
  const { token } = await api<{ token: string }>('/auth/forget-password/phone/verify', {
    method: 'POST',
    body: { phone: normalizePhone(phone) ?? phone, code },
  });
  return token;
}

/**
 * Set, correct or clear the user's own email address. Backend: PATCH
 * /users/me/email. Passing null clears an unverified address; the backend
 * refuses any change once the address is verified.
 */
export async function updateMyEmail(email: string | null): Promise<void> {
  await api('/users/me/email', { method: 'PATCH', body: { email } });
}

export async function logout(): Promise<void> {
  await api('/auth/logout', { method: 'POST' });
}

/**
 * Self-service profile update (collected from the profile screen since
 * register doesn't capture location; feeds listings + seller profiles).
 * Sends ids only — the server looks the names up itself, the same rule
 * createListing follows — and always sends all three levels so a level left
 * unselected explicitly clears itself and everything below it, rather than
 * leaving a stale district under a newly cleared province. Backend: PATCH
 * /users/me. Callers refresh the session afterwards (AuthContext.refresh) so
 * the new location shows everywhere.
 */
export async function updateMyLocation(selection: LocationSelection): Promise<void> {
  await api('/users/me', {
    method: 'PATCH',
    body: {
      county_code: selection.countyCode ?? null,
      town_id: selection.townId ?? null,
      neighbourhood_id: selection.neighbourhoodId ?? null,
    },
  });
}

/**
 * The user's own passive switch. Deliberately reachable while the
 * account is already passive — it is the only way back.
 */
export async function setMyAccountActive(isActive: boolean): Promise<void> {
  await api('/users/me/active', { method: 'PUT', body: { is_active: isActive } });
}

/**
 * KVKK deletion. Irreversible. The route takes no id, so it can only ever
 * reach the caller's own account; the phone confirms intent and the password
 * (or the token from a finished Google re-auth) confirms identity.
 */
export async function deleteMyAccount(input: {
  phone: string;
  password?: string;
  googleToken?: string;
}): Promise<void> {
  await api('/users/me', { method: 'DELETE', body: input });
}

/** Still fixture data — achievement badges have no backend yet. */
export async function getMyBadges(): Promise<AchievementBadge[]> {
  await fakeDelay();
  return clone(achievementBadges);
}
